import React from "react";
import { useAdminPermissions } from "../../hooks/useAdminPermissions";
import { getAdminNavItemByTo } from "../../../config/adminNavigationCatalog";

type Props = {
	/** Explicit permission key. Overrides catalog lookup. */
	permission?: string | null;
	/** Path to look up in adminNavigationCatalog. Used if `permission` not provided. */
	path?: string;
	children: React.ReactNode;
};

/**
 * Gate page content by RBAC permission.
 * Usage:
 *   Inside a route, wrap children:
 *     <PermissionRoute path="/booking">
 *       <BookingPage />
 *     </PermissionRoute>
 *
 * Resolves the required permission from ADMIN_NAV_CATALOG by matching path.
 * If user lacks permission, renders a 403 page. While permissions are loading,
 * renders a spinner placeholder.
 */
export default function PermissionRoute({ permission, path, children }: Props) {
	const { hasPermission, loaded } = useAdminPermissions();

	const requiredKey =
		permission !== undefined ? permission : path ? getAdminNavItemByTo(path)?.permission ?? null : null;

	// No permission required → always allow
	if (!requiredKey) return <>{children}</>;

	if (!loaded) {
		return (
			<div style={{ padding: "60px 20px", textAlign: "center", color: "#888" }}>
				Loading your access…
			</div>
		);
	}

	if (!hasPermission(requiredKey)) {
		return (
			<div style={{ padding: "80px 20px", textAlign: "center", maxWidth: 520, margin: "0 auto" }}>
				<div style={{ fontSize: 64, marginBottom: 16 }}>🔒</div>
				<h2 style={{ marginBottom: 12, color: "#333" }}>Access denied</h2>
				<p style={{ color: "#666", marginBottom: 6 }}>
					Requires permission:{" "}
					<code style={{ background: "#f5f5f5", padding: "2px 8px", borderRadius: 4 }}>{requiredKey}</code>
				</p>
				<p style={{ color: "#666", marginBottom: 24, fontSize: 14 }}>
					You don&apos;t have access to this tool yet. Access is managed in Workspace — please contact HR to
					get a role assigned.
				</p>
				<a
					href="/"
					style={{
						display: "inline-block",
						padding: "10px 20px",
						background: "#4f46e5",
						color: "white",
						borderRadius: 6,
						textDecoration: "none",
					}}>
					← Back to admin home
				</a>
			</div>
		);
	}

	return <>{children}</>;
}
